import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "@phosphor-icons/react";

const STORAGE_KEY = "housetag_cookie_prefs";
const STORAGE_VERSION = 1;

const DEFAULT_PREFS = {
  version: STORAGE_VERSION,
  essential: true, // sempre true, non modificabile
  analytics: false,
  marketing: false,
  preferences: false,
  timestamp: null,
};

export function getCookiePrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && p.version === STORAGE_VERSION) return p;
    return null;
  } catch {
    return null;
  }
}

function saveCookiePrefs(prefs) {
  const payload = {
    ...DEFAULT_PREFS,
    ...prefs,
    essential: true,
    version: STORAGE_VERSION,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    // Ritarda per non bloccare il first paint
    const t = setTimeout(() => {
      const existing = getCookiePrefs();
      if (!existing) setVisible(true);
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const acceptAll = () => {
    saveCookiePrefs({ analytics: true, marketing: true, preferences: true });
    setVisible(false);
  };

  const rejectNonEssential = () => {
    saveCookiePrefs({ analytics: false, marketing: false, preferences: false });
    setVisible(false);
  };

  const savePreferences = () => {
    saveCookiePrefs(prefs);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-4"
      role="dialog"
      aria-live="polite"
      aria-label="Consenso cookie"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        {/* Header compatto */}
        <div className="flex items-start gap-3 px-5 pt-4 pb-3">
          <div className="hidden sm:flex h-10 w-10 rounded-full bg-purple-50 items-center justify-center shrink-0">
            <Cookie size={22} className="text-purple-700" weight="fill" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-base sm:text-lg font-bold tracking-tight text-slate-900">
              Rispettiamo la tua privacy
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
              Usiamo cookie tecnici essenziali per far funzionare Housetag e, con il tuo consenso, cookie di
              analytics, marketing e preferenze per migliorare la tua esperienza. Puoi accettare tutto,
              rifiutare i non essenziali o personalizzare le tue scelte. Leggi la nostra{" "}
              <Link to="/privacy" className="text-purple-700 underline font-medium" data-testid="cookie-banner-privacy-link">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={rejectNonEssential}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            aria-label="Chiudi banner (rifiuta non essenziali)"
            data-testid="cookie-banner-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Dettagli categorie */}
        {showDetails && (
          <div className="px-5 pb-1 space-y-2 border-t border-slate-100 pt-3" data-testid="cookie-banner-details">
            <CategoryRow
              testid="cookie-cat-essential"
              title="Essenziali"
              description="Necessari per login, sessione e funzionalità di base. Sempre attivi."
              checked
              disabled
            />
            <CategoryRow
              testid="cookie-cat-analytics"
              title="Analytics"
              description="Ci aiutano a capire come viene usata Housetag e a migliorarla in modo anonimo."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
            <CategoryRow
              testid="cookie-cat-marketing"
              title="Marketing"
              description="Consentono di mostrarti contenuti e offerte più rilevanti sui nostri canali."
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
            />
            <CategoryRow
              testid="cookie-cat-preferences"
              title="Preferenze"
              description="Ricordano le tue impostazioni (lingua, layout) per un'esperienza personalizzata."
              checked={prefs.preferences}
              onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
            />
          </div>
        )}

        {/* Pulsanti azione */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
          {!showDetails ? (
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-white transition-colors"
              data-testid="cookie-banner-customize"
            >
              Personalizza
            </button>
          ) : (
            <button
              type="button"
              onClick={savePreferences}
              className="text-sm font-medium text-slate-700 hover:text-slate-900 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 transition-colors"
              data-testid="cookie-banner-save-prefs"
            >
              Salva preferenze
            </button>
          )}
          <button
            type="button"
            onClick={rejectNonEssential}
            className="text-sm font-medium text-slate-700 hover:text-slate-900 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 transition-colors"
            data-testid="cookie-banner-reject"
          >
            Rifiuta non essenziali
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="text-sm font-semibold text-white px-4 py-2 rounded-lg bg-purple-700 hover:bg-purple-800 transition-colors shadow-sm"
            data-testid="cookie-banner-accept-all"
          >
            Accetta tutti
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ title, description, checked, onChange, disabled = false, testid }) {
  return (
    <label
      className={`flex items-start gap-3 py-2 ${disabled ? "opacity-70" : "cursor-pointer"}`}
      data-testid={testid}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-purple-700 focus:ring-purple-500 disabled:cursor-not-allowed"
        data-testid={`${testid}-checkbox`}
      />
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">
          {title}
          {disabled && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 font-medium">Sempre attivi</span>}
        </div>
        <div className="text-xs text-slate-600 leading-relaxed">{description}</div>
      </div>
    </label>
  );
}
